import React from 'react';
import {motion} from 'framer-motion';
import {useLanguage} from '../contexts/LanguageContext';
import Header from '../components/Header';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiShield, FiArrowLeft} = FiIcons;

const CONTENT = {
  zh: {
    title: '隱私政策',
    lastUpdated: '最後更新：2026年3月7日',
    sections: [
      {heading: '1. 我們不收集您的文件', body: 'Convert Master 的所有文件處理均在您的瀏覽器中本地完成。您上傳的 PDF、圖片等文件不會傳送到我們的伺服器。我們無法存取您的文件內容。'},
      {heading: '2. 我們不收集個人資料', body: '我們不要求您註冊帳號，不收集您的姓名、電子郵件或其他個人識別資訊。'},
      {heading: '3. 本地存儲', body: '我們僅使用 localStorage 儲存您的語言偏好設置（例如：cm-language）。此資料僅存在您的設備上，不會傳送到我們的伺服器。'},
      {heading: '4. 第三方服務', body: '我們使用 Stripe 處理贊助付款。Stripe 有其獨立的隱私政策。我們使用 Google Fonts 載入字體。CDN 可能記錄標準的請求日誌（IP 地址、時間戳）。'},
      {heading: '5. Cookies', body: '我們不使用追蹤 Cookie。我們不使用廣告 Cookie。我們不使用分析 Cookie（如 Google Analytics）。'},
      {heading: '6. 數據安全', body: '由於所有處理都在您的設備上進行，您的文件數據從不離開您的設備。關閉瀏覽器頁面後，所有處理過的文件數據將被立即清除。'},
      {heading: '7. 聯繫我們', body: '如有任何隱私相關問題，請聯繫：info@homy.hk'},
    ],
  },
  en: {
    title: 'Privacy Policy',
    lastUpdated: 'Last Updated: March 7, 2026',
    sections: [
      {heading: '1. We Do Not Collect Your Files', body: 'All file processing in Convert Master is done locally in your browser. PDF, image, and other files you use are never sent to our servers. We have no access to your file contents.'},
      {heading: '2. We Do Not Collect Personal Data', body: 'We do not require account registration and do not collect your name, email address, or other personally identifiable information.'},
      {heading: '3. Local Storage', body: 'We only use localStorage to save your language preference (e.g., cm-language). This data exists only on your device and is never transmitted to our servers.'},
      {heading: '4. Third-Party Services', body: 'We use Stripe to process sponsorship payments. Stripe has its own privacy policy. We use Google Fonts for typography. CDNs may log standard request logs (IP address, timestamps).'},
      {heading: '5. Cookies', body: 'We do not use tracking cookies. We do not use advertising cookies. We do not use analytics cookies (such as Google Analytics).'},
      {heading: '6. Data Security', body: 'Since all processing occurs on your device, your file data never leaves your device. After closing the browser tab, all processed file data is immediately cleared from memory.'},
      {heading: '7. Contact Us', body: 'For any privacy-related questions, please contact: info@homy.hk'},
    ],
  },
  ja: {
    title: 'プライバシーポリシー',
    lastUpdated: '最終更新：2026年3月7日',
    sections: [
      {heading: '1. ファイルは収集しません', body: 'Convert Master のすべてのファイル処理はブラウザ内でローカルに行われます。PDF、画像などのファイルは当社のサーバーに送信されることはありません。'},
      {heading: '2. 個人情報は収集しません', body: 'アカウント登録は不要で、お名前、メールアドレス、その他の個人識別情報は収集しません。'},
      {heading: '3. ローカルストレージ', body: '言語設定（cm-language）を保存するためにのみ localStorage を使用します。このデータはお使いのデバイスにのみ存在し、サーバーには送信されません。'},
      {heading: '4. サードパーティサービス', body: 'スポンサーシップ支払いの処理にStripeを使用しています。StripeはStripe独自のプライバシーポリシーを持っています。Google Fontsを使用しています。'},
      {heading: '5. Cookie', body: 'トラッキングCookieは使用しません。広告Cookieは使用しません。分析Cookie（Google Analyticsなど）は使用しません。'},
      {heading: '6. データセキュリティ', body: 'すべての処理がお使いのデバイスで行われるため、ファイルデータがデバイスから出ることはありません。ブラウザのタブを閉じると、処理されたすべてのファイルデータは即座にメモリから消去されます。'},
      {heading: '7. お問い合わせ', body: 'プライバシーに関するご質問は、こちらまでお問い合わせください：info@homy.hk'},
    ],
  },
  es: {
    title: 'Política de Privacidad',
    lastUpdated: 'Última actualización: 7 de marzo de 2026',
    sections: [
      {heading: '1. No Recopilamos Tus Archivos', body: 'Todo el procesamiento de archivos en Convert Master se realiza localmente en tu navegador. Los archivos PDF, imágenes y otros que uses nunca se envían a nuestros servidores.'},
      {heading: '2. No Recopilamos Datos Personales', body: 'No requerimos registro de cuenta y no recopilamos tu nombre, dirección de correo electrónico u otra información de identificación personal.'},
      {heading: '3. Almacenamiento Local', body: 'Solo usamos localStorage para guardar tu preferencia de idioma (por ejemplo, cm-language). Estos datos solo existen en tu dispositivo y nunca se transmiten a nuestros servidores.'},
      {heading: '4. Servicios de Terceros', body: 'Usamos Stripe para procesar pagos de patrocinio. Stripe tiene su propia política de privacidad. Usamos Google Fonts para tipografía.'},
      {heading: '5. Cookies', body: 'No usamos cookies de seguimiento. No usamos cookies publicitarias. No usamos cookies de análisis (como Google Analytics).'},
      {heading: '6. Seguridad de Datos', body: 'Dado que todo el procesamiento ocurre en tu dispositivo, los datos de tus archivos nunca salen de tu dispositivo. Al cerrar la pestaña del navegador, todos los datos de archivos procesados se borran inmediatamente de la memoria.'},
      {heading: '7. Contáctanos', body: 'Para cualquier pregunta relacionada con la privacidad, contáctanos en: info@homy.hk'},
    ],
  },
};

const PrivacyPage = () => {
  const {currentLang} = useLanguage();
  const content = CONTENT[currentLang] || CONTENT.en;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.4}}
        >
          <a
            href="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-6 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            {currentLang === 'ja' ? 'ホームに戻る' : currentLang === 'es' ? 'Volver al inicio' : currentLang === 'zh' ? '返回首頁' : 'Back to Home'}
          </a>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-8 py-10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiShield} className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">{content.title}</h1>
                  <p className="text-white/70 text-sm mt-1">{content.lastUpdated}</p>
                </div>
              </div>
            </div>

            <div className="px-8 py-8 space-y-6">
              {content.sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{opacity: 0, y: 10}}
                  animate={{opacity: 1, y: 0}}
                  transition={{delay: index * 0.05}}
                >
                  <h2 className="text-base font-semibold text-gray-800 mb-2">{section.heading}</h2>
                  <p className="text-sm text-gray-600 leading-relaxed">{section.body}</p>
                </motion.div>
              ))}

              <div className="border-t border-gray-100 pt-6 text-center">
                <p className="text-xs text-gray-400">
                  © {new Date().getFullYear()} Convert Master · Homy Tech Limited · info@homy.hk
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPage;